import { forwardRef, useEffect } from 'react'
import { MdOutlineSearch } from 'react-icons/md'
import { Configure, InstantSearch, useInstantSearch } from 'react-instantsearch'

import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { InstantSearchApi } from 'react-instantsearch'
import type { Props as RefinementListProps } from '~/components/Algolia/RefinementList'
import type { TableWrapperProps } from '~/components/Tables/TableWrapper'

import Refinements from './Refinements'

import AlgoliaHitsPerPage from '../HitsPerPage'
import AlgoliaPagination from '../Pagination'
import AlgoliaSearch from '../Search'

import TableWrapper from '~/components/Tables/TableWrapper'
import { getSearchClient } from '~/utils/algolia'

import './styles.scss'

const searchClient = getSearchClient()

interface Props extends Omit<TableWrapperProps, 'rowData'> {
  configurationFilters?: string
  hitsPerPage: number
  indexName: string
  onInit?: (instantSearchApi: InstantSearchApi) => void
  refinements?: RefinementListProps[]
}

const Table = forwardRef<AgGridReact, Omit<Props, 'indexName' | 'configurationFilters'>>(
  ({ hitsPerPage, onInit, refinements, ...props }, ref) => {
    const instantSearch = useInstantSearch()

    const rowData = instantSearch.results.hits

    useEffect(() => {
      onInit && onInit(instantSearch)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <>
        {refinements ? <Refinements className="algolia-table-component__side" refinements={refinements} /> : null}
        <div className="algolia-table-component__body">
          <div className="algolia-table-component__body__header search-container">
            <div className="table-search">
              <AlgoliaSearch
                autoFocus={props.withSearchBarAutoFocus}
                icon={<MdOutlineSearch size={16} />}
                placeholder="Search"
              />
            </div>
            <AlgoliaHitsPerPage
              entriesName={props.entriesName}
              items={[{ default: true, label: '', value: hitsPerPage }]}
            />
          </div>
          <div className="algolia-table-component__body__results">
            <TableWrapper {...props} ref={ref} rowData={rowData} withPagination={false} />
            <AlgoliaPagination />
          </div>
        </div>
      </>
    )
  }
)

Table.displayName = 'Table'

const AlgoliaTable = forwardRef<AgGridReact, Props>(
  ({ configurationFilters, indexName, ...props }, ref): ReactElement => {
    return (
      <div className="algolia-table-component">
        <InstantSearch indexName={indexName} searchClient={searchClient}>
          <Configure filters={configurationFilters} hitsPerPage={props.hitsPerPage} />
          <Table {...props} ref={ref} />
        </InstantSearch>
      </div>
    )
  }
)

AlgoliaTable.displayName = 'AlgoliaTable'

export default AlgoliaTable
